import { Plus, MessageSquare, Server } from "lucide-react";
import type { Thread, MCPServer } from "@/types";
import { formatDistanceToNow } from "date-fns";

interface ThreadListProps {
  threads: Thread[];
  selectedThreadId?: string;
  server?: MCPServer;
  servers?: MCPServer[]; // All servers for displaying server name in thread items
  onSelectThread: (threadId: string) => void;
  onNewThread: () => void;
}

export const ThreadList = ({
  threads,
  selectedThreadId,
  server,
  servers = [],
  onSelectThread,
  onNewThread,
}: ThreadListProps) => {
  // Helper to get server info for a thread
  const getServerForThread = (thread: Thread) => {
    return servers.find(s => s.id === thread.serverId);
  };
  if (!server) {
    return (
      <section className="glass-card flex min-h-[60vh] flex-col items-center justify-center gap-2 sm:gap-3 border border-white/40 p-4 sm:p-6 text-center shadow-xl dark:border-white/10">
        <p className="text-gradient text-base sm:text-lg font-semibold">Select a server to view threads</p>
        <p className="max-w-md text-xs sm:text-sm text-muted-foreground px-2">
          Pick an MCP server from your contacts to see conversation threads and start chatting.
        </p>
      </section>
    );
  }

  return (
    <section className="glass-card flex flex-col gap-3 sm:gap-4 border border-white/40 p-3 sm:p-4 shadow-xl dark:border-white/10">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-base sm:text-lg font-semibold text-foreground">
            {server.name} Threads
          </h2>
          <p className="text-xs sm:text-sm text-muted-foreground">
            {threads.length} {threads.length === 1 ? 'conversation' : 'conversations'}
          </p>
        </div>
        <button
          type="button"
          onClick={onNewThread}
          data-testid="new-chat-button"
          className="inline-flex items-center gap-1.5 sm:gap-2 rounded-lg border border-primary/40 bg-primary/10 px-2.5 py-1.5 sm:px-3 sm:py-2 text-xs sm:text-sm font-medium text-primary shadow-sm transition hover:bg-primary/20 hover:shadow-md"
        >
          <Plus className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
          <span>New Chat</span>
        </button>
      </div>

      {threads.length === 0 ? (
        <div className="flex flex-col items-center justify-center gap-2 sm:gap-3 py-8 sm:py-12 text-center">
          <MessageSquare className="h-10 w-10 sm:h-12 sm:w-12 text-muted-foreground/50" />
          <p className="text-sm sm:text-base font-medium text-muted-foreground">
            No chats yet. Start a conversation!
          </p>
          <button
            type="button"
            onClick={onNewThread}
            className="mt-2 inline-flex items-center gap-2 rounded-lg border border-primary/40 bg-primary/10 px-3 py-2 text-sm font-medium text-primary shadow-sm transition hover:bg-primary/20"
          >
            <Plus className="h-4 w-4" />
            Create First Thread
          </button>
        </div>
      ) : (
        <div className="flex flex-col gap-2 max-h-[60vh] overflow-y-auto">
          {threads.map(thread => {
            const threadServer = getServerForThread(thread);
            return (
              <button
                key={thread.id}
                type="button"
                data-testid={`thread-item-${thread.id}`}
                onClick={() => onSelectThread(thread.id)}
                className={`flex flex-col gap-1 sm:gap-1.5 rounded-lg border p-2.5 sm:p-3 text-left transition ${
                  selectedThreadId === thread.id
                    ? 'border-primary bg-primary/10 shadow-md'
                    : 'border-border/60 bg-white/70 hover:border-primary/40 hover:bg-white/90 dark:bg-white/5'
                }`}
              >
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-1.5 min-w-0 flex-1">
                    {threadServer && (
                      <div className="flex-shrink-0">
                        <Server className="h-3 w-3 sm:h-3.5 sm:w-3.5 text-primary/70" />
                      </div>
                    )}
                    <span className="text-xs sm:text-sm font-medium text-foreground truncate" data-testid={`thread-title-${thread.id}`}>
                      {thread.name || 'New Conversation'}
                    </span>
                  </div>
                  <span className="text-[10px] sm:text-xs text-muted-foreground whitespace-nowrap" data-testid={`thread-timestamp-${thread.id}`}>
                    {formatDistanceToNow(new Date(thread.updatedAt), { addSuffix: true })}
                  </span>
                </div>
                {threadServer && (
                  <p className="text-[10px] sm:text-xs text-primary/60 font-medium truncate" data-testid={`thread-server-${thread.id}`}>
                    {threadServer.name}
                  </p>
                )}
                {thread.lastMessagePreview && (
                  <p className="text-[10px] sm:text-xs text-muted-foreground truncate" data-testid={`thread-preview-${thread.id}`}>
                    {thread.lastMessagePreview}
                  </p>
                )}
              </button>
            );
          })}
        </div>
      )}
    </section>
  );
};
